import type { ReactNode } from 'react';
import type { Escrita } from '../../giro';
import { Icone } from '../../design/Icone';
import type { AvisoDoMural, PublicoDoAviso, TipoDeAviso } from '../../administracao';
import './mural.css';

/**
 * TELAS 29 e 30 — o que as duas telas (e a Home do balconista) dividem: datas no fuso da
 * operação, os ícones que o desenho usa e a Icone ainda não tem, o desfecho das escritas e o
 * cartão do aviso. O cartão é UM só: a prévia do "Novo aviso" usa o mesmo componente que o
 * balconista vê, então a prévia não mente.
 */

/* ------------------------------ datas (Brasília) ------------------------------ */

const FUSO = 'America/Sao_Paulo';
const partes = (iso: string) => {
  const p = new Intl.DateTimeFormat('pt-BR', {
    timeZone: FUSO,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(iso));
  const v = (t: string) => p.find((x) => x.type === t)?.value ?? '';
  return {
    d: v('day'),
    m: v('month'),
    a: v('year'),
    h: v('hour') === '24' ? '00' : v('hour'),
    mi: v('minute'),
  };
};

/** "01/10" */
export const diaMes = (iso: string) => {
  const p = partes(iso);
  return `${p.d}/${p.m}`;
};
/** "01/10 00:00" */
export const diaMesHora = (iso: string) => {
  const p = partes(iso);
  return `${p.d}/${p.m} ${p.h}:${p.mi}`;
};
/** "01/09/2026" */
export const diaMesAno = (iso: string) => {
  const p = partes(iso);
  return `${p.d}/${p.m}/${p.a}`;
};
/** "01–07/10" · "28/09–05/10" · "a definir" */
export function periodoCurto(inicio: string | null, fim: string | null): string {
  if (!inicio || !fim) return 'a definir';
  const a = partes(inicio);
  const b = partes(fim);
  if (a.m === b.m && a.a === b.a) return `${a.d}–${b.d}/${b.m}`;
  return `${a.d}/${a.m}–${b.d}/${b.m}`;
}
/** "22/09 14:00 → 28/09 23:59" · "a definir" */
export function vigencia(inicio: string | null, fim: string | null): string {
  if (!inicio && !fim) return 'a definir';
  if (!inicio) return `ao publicar → ${diaMesHora(fim ?? '')}`;
  if (!fim) return `${diaMesHora(inicio)} → sem fim`;
  return `${diaMesHora(inicio)} → ${diaMesHora(fim)}`;
}
/** ISO → valor de <input type="datetime-local"> no fuso da operação. */
export function paraCampoDataHora(iso: string | null): string {
  if (!iso) return '';
  const p = partes(iso);
  return `${p.a}-${p.m}-${p.d}T${p.h}:${p.mi}`;
}
/** Valor do campo → ISO com o fuso de Brasília escrito (o servidor não adivinha o fuso). */
export function deCampoDataHora(v: string): string | null {
  return v ? `${v}:00-03:00` : null;
}

/* ------------------------------ ícones do desenho ------------------------------ */

const EXTRAS = {
  tag: 'M10.4 2.8H16v5.6l-7.6 7.6-5.6-5.6ZM13.2 5.6v.1',
  pause: 'M7 4.2v11.6M13 4.2v11.6',
  layers: 'M10 2.8 17.4 6.6 10 10.4 2.6 6.6ZM2.6 10.4 10 14.2l7.4-3.8M2.6 13.8 10 17.6l7.4-3.8',
  info: 'M10 17.2a7.2 7.2 0 1 0 0-14.4 7.2 7.2 0 0 0 0 14.4ZM10 9v4.6M10 6.4v.1',
  play: 'M6.4 3.8 15.6 10l-9.2 6.2Z',
  fixar: 'M7.4 2.8h5.2l-.9 5.2 3 3H5.3l3-3ZM10 11v6.2',
} as const;
export type IconeExtraNome = keyof typeof EXTRAS;

export function IconeExtra({
  nome,
  tamanho = 16,
  traco = 1.6,
}: {
  nome: IconeExtraNome;
  tamanho?: number;
  traco?: number;
}) {
  return (
    <svg
      width={tamanho}
      height={tamanho}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={nome === 'pause' ? 2.2 : traco}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flex: 'none' }}
    >
      <path d={EXTRAS[nome]} />
    </svg>
  );
}

/* ------------------------------ desfecho das escritas ------------------------------ */

/**
 * Recusa mostra a mensagem do servidor. INCERTA nunca vira recusa: a rede caiu no meio e pode
 * ter gravado, então a pessoa confere antes de tentar de novo.
 */
export function Desfecho<T>({
  escrita,
  incerta,
  aoConferir,
}: {
  escrita: Escrita<T> | null;
  incerta: string;
  aoConferir?: () => void;
}) {
  if (!escrita || escrita.tipo === 'confirmada') return null;
  if (escrita.tipo === 'recusada')
    return (
      <div className="rm-desfecho rm-recusada" role="alert">
        <Icone nome="x" tamanho={14} traco={1.8} />
        <span>{escrita.mensagem}</span>
      </div>
    );
  return (
    <div className="rm-desfecho rm-incerta" role="status">
      <Icone nome="warn" tamanho={14} traco={1.7} />
      <span>
        <b>Sem resposta do servidor.</b> {incerta}
        {aoConferir && (
          <>
            {' '}
            <button type="button" className="ac-link" onClick={aoConferir}>
              Conferir agora
            </button>
          </>
        )}
      </span>
    </div>
  );
}

/* ------------------------------ o cartão do aviso ------------------------------ */

export const ROTULO_DO_TIPO: Readonly<Record<TipoDeAviso, string>> = {
  INFORMACAO: 'Informação',
  ATENCAO: 'Atenção',
  CAMPANHA: 'Campanha',
  OPERACIONAL: 'Operacional',
  URGENTE: 'Urgente',
};
export const ROTULO_DO_PUBLICO: Readonly<Record<PublicoDoAviso, string>> = {
  TODOS: 'Todos',
  BALCONISTAS: 'Balconistas',
  ADMINISTRADORES: 'Administradores',
  AUDITORES: 'Auditores',
};

export function IconeDoTipo({ tipo, tamanho = 13 }: { tipo: TipoDeAviso; tamanho?: number }) {
  if (tipo === 'URGENTE' || tipo === 'ATENCAO')
    return <Icone nome="warn" tamanho={tamanho} traco={1.8} />;
  if (tipo === 'CAMPANHA') return <IconeExtra nome="tag" tamanho={tamanho} traco={1.7} />;
  if (tipo === 'OPERACIONAL') return <Icone nome="cycle" tamanho={tamanho} traco={1.7} />;
  return <IconeExtra nome="info" tamanho={tamanho} traco={1.7} />;
}

type ConteudoDoAviso = Pick<
  AvisoDoMural,
  'titulo' | 'corpo' | 'tipo' | 'fixado' | 'exigeConfirmacao' | 'fim'
> &
  Partial<Pick<AvisoDoMural, 'confirmadoEm' | 'lidoEm'>>;

/**
 * O aviso como quem LÊ vê. `resumido`: só o título e a primeira linha (Home). Urgente ganha a
 * borda vermelha; nada pisca, nada cobre a tela.
 */
export function CartaoDeAviso({
  aviso,
  resumido = false,
  aberto = true,
  aoAbrir,
  href,
  confirmar,
  rodape,
}: {
  aviso: ConteudoDoAviso;
  resumido?: boolean;
  aberto?: boolean;
  aoAbrir?: () => void;
  /** Na Home o título leva ao mural, onde abrir conta como "lido". */
  href?: string;
  /** Botão "Li e entendi" — só quando o aviso pede e a pessoa ainda não confirmou. */
  confirmar?: ReactNode;
  rodape?: ReactNode;
}) {
  const corpo = resumido ? (aviso.corpo.split('\n')[0] ?? '') : aviso.corpo;
  return (
    <article className={`mu-cartao mu-cartao-${aviso.tipo.toLowerCase()}`}>
      <div className="mu-cartao-topo">
        <span className="mu-cartao-tipo">
          <IconeDoTipo tipo={aviso.tipo} />
          {ROTULO_DO_TIPO[aviso.tipo].toUpperCase()}
        </span>
        <span className="ac-espaco" />
        {aviso.lidoEm === null && !resumido && <span className="mu-novo">novo</span>}
        {aviso.fixado && (
          <span className="mu-fixado" title="Fixado no topo">
            <IconeExtra nome="fixar" tamanho={14} traco={1.6} />
            <span className="mu-sr">Fixado no topo</span>
          </span>
        )}
      </div>
      {href ? (
        <a className="mu-cartao-titulo mu-cartao-link" href={href}>
          {aviso.titulo || 'Título do aviso'}
        </a>
      ) : aoAbrir ? (
        <button
          type="button"
          className="mu-cartao-titulo mu-cartao-botao"
          aria-expanded={aberto}
          onClick={aoAbrir}
        >
          {aviso.titulo || 'Título do aviso'}
        </button>
      ) : (
        <div className="mu-cartao-titulo">{aviso.titulo || 'Título do aviso'}</div>
      )}
      {aberto && corpo && (
        <div className={`mu-cartao-corpo${resumido ? ' mu-cartao-corpo-curto' : ''}`}>{corpo}</div>
      )}
      {aberto && !resumido && aviso.fim && (
        <div className="mu-cartao-validade">vale até {diaMesHora(aviso.fim)}</div>
      )}
      {confirmar}
      {aviso.confirmadoEm && (
        <div className="mu-confirmado">
          <Icone nome="check" tamanho={14} traco={1.9} />
          Leitura confirmada {diaMesHora(aviso.confirmadoEm)}
        </div>
      )}
      {rodape}
    </article>
  );
}
